// src/cn/pages/MemoTalk/MemoTalkPageCN.jsx
import { useEffect, useRef, useState } from "react";
import { MemoTalkAI } from "../../../lib/memotalkApi";

function getLang() {
  return (typeof document !== "undefined" && document.documentElement?.lang) || "ko";
}

export default function MemoTalkPageCN() {
  const lang = getLang();

  // 입력
  const [memoInput, setMemoInput] = useState("");
  // 결과
  const [memoResult, setMemoResult] = useState("");
  const [messageResult, setMessageResult] = useState("");
  // 상태
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const alertRef = useRef(null);

  // 레거시 커스텀 알림 (fade in/out)
  function showCustomAlert(message) {
    if (!alertRef.current) return;
    alertRef.current.textContent = message;
    alertRef.current.classList.add("show");
    setTimeout(() => {
      alertRef.current && alertRef.current.classList.remove("show");
    }, 2000);
  }

  async function handleGenerate() {
    const text = memoInput.trim();

    const msgInputEmpty = lang === "zh-CN" ? "请输入备忘录内容！" : "메모 내용을 입력해주세요!";
    const btnProcessingText = lang === "zh-CN" ? "AI 正在整理中..." : "AI가 정리하는 중...";
    const btnDefaultText = lang === "zh-CN" ? "使用 AI 整理备忘录" : "AI로 메모 정리하기";
    const msgError = lang === "zh-CN" ? "AI 调用过程中发生错误。请稍后再试。" : "AI 호출 중 오류가 발생했습니다. 잠시 후 다시 시도해주세요.";

    if (!text) {
      setError(msgInputEmpty);
      setTimeout(() => setError(""), 3000);
      return;
    }

    setBusy(true);
    setError("");
    setMemoResult("");
    setMessageResult("");

    // 버튼 텍스트 동기화 위해 data-state 사용 (간단)
    const btn = document.getElementById("memoTalk-generate-btn");
    const btnSpan = btn?.querySelector("span");
    if (btnSpan) btnSpan.textContent = btnProcessingText;

    try {
      const { memo, message } = await MemoTalkAI.generate({ memoText: text });
      setMemoResult(memo);
      setMessageResult(message);
    } catch (e) {
      console.error(e);
      setError(msgError);
    } finally {
      setBusy(false);
      if (btnSpan) btnSpan.textContent = btnDefaultText;
    }
  }

  async function handleCopy(target) {
    const text = target === "memo" ? memoResult : messageResult;
    if (!text) return;

    let msgSuccess = lang === "zh-CN" ? "已复制！" : "복사되었습니다!";
    let msgFail = lang === "zh-CN" ? "复制失败。" : "복사에 실패했습니다.";

    try {
      await navigator.clipboard.writeText(text);
      showCustomAlert(msgSuccess);
    } catch {
      try {
        const ta = document.createElement("textarea");
        ta.value = text;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
        showCustomAlert(msgSuccess);
      } catch (err) {
        console.error("copy fail:", err);
        showCustomAlert(msgFail);
      }
    }
  }

  const btnLabel = lang === "zh-CN" ? "使用 AI 整理备忘录" : "AI로 메모 정리하기";
  const copyLabel = lang === "zh-CN" ? "复制" : "복사";

  return (
    <section className="memoTalk">
      {/* 상단 타이틀 영역 (기존 전환 패턴) */}
      <div id="content_title">
        <h2>AI 备忘录整理 & 通知톡撰写助手</h2>
        <p>输入咨询后匆忙记录的内容，AI 将一次性生成 CRM 备忘录与发送给客户的通知톡文案！</p>
      </div>

      <div className="content">
        <div>
          <div>
            {/* 입력 */}
            <div>
              <textarea
                rows={8}
                className="textarea"
                placeholder={`请在这里自由输入咨询内容。（例如：金敏智患者，希望今天下午3点预约。最近消化不好且很疲惫。咨询了脂肪四药2周份。）`}
                value={memoInput}
                onChange={(e) => setMemoInput(e.target.value)}
              />
            </div>

            {/* 실행 버튼 + 로더/에러 */}
            <button id="memoTalk-generate-btn" className="btn submitbtn" onClick={handleGenerate} disabled={busy}>
              <span>{btnLabel}</span>
            </button>

            {busy && <div className="loader mt-4" />}

            {error && (
              <div className="trf-error" style={{ textAlign: "center", color: "#dc2626", marginTop: "0.75rem" }}>
                {error}
              </div>
            )}

            {/* 결과 */}
            {(memoResult || messageResult) && (
              <div>
                <div>
                  <div>
                    <h4>📋 可复制到 CRM 的备忘录</h4>
                    <p>{memoResult}</p>

                    <button className="copy" onClick={() => handleCopy("memo")}>
                      {copyLabel}
                    </button>
                  </div>
                </div>
                <div>
                  <div>
                    <h4>📱 发送给客户的通知톡</h4>
                    <p>{messageResult}</p>

                    <button className="copy" onClick={() => handleCopy("message")}>
                      {copyLabel}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 커스텀 알림 토스트 */}
      <div ref={alertRef} className="custom-alert" />
    </section>
  );
}
